import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { X, Save, Loader2 } from 'lucide-react';
import { RoomPlan } from '../../services/roomService';

interface RoomPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<RoomPlan>) => Promise<void>;
  roomPlan?: RoomPlan | null;
}

const RoomPlanModal: React.FC<RoomPlanModalProps> = ({ isOpen, onClose, onSave, roomPlan }) => {
  const [formData, setFormData] = useState<Partial<RoomPlan>>({
    plan_name: '',
    meal_plan: 'Room Only',
    meal_plan_description: '',
    base_rate: 0,
    adults_included: 1,
    children_included: 0,
    occupancy_type: 'Double',
    is_refundable: true,
    status: 'active'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (roomPlan) {
      setFormData({
        plan_name: roomPlan.plan_name,
        meal_plan: roomPlan.meal_plan,
        meal_plan_description: roomPlan.meal_plan_description,
        base_rate: roomPlan.base_rate,
        adults_included: roomPlan.adults_included,
        children_included: roomPlan.children_included,
        occupancy_type: roomPlan.occupancy_type,
        is_refundable: roomPlan.is_refundable,
        status: roomPlan.status
      });
    } else {
      setFormData({
        plan_name: '',
        meal_plan: 'Room Only',
        meal_plan_description: '',
        base_rate: 0,
        adults_included: 1,
        children_included: 0,
        occupancy_type: 'Double',
        is_refundable: true,
        status: 'active'
      });
    }
    setError(null);
  }, [roomPlan, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.plan_name?.trim()) {
      setError('Plan name is required');
      return;
    }
    if (!formData.base_rate || formData.base_rate <= 0) {
      setError('Base rate must be greater than 0');
      return;
    }
    if (!formData.adults_included || formData.adults_included <= 0) {
      setError('Adults included must be greater than 0');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onSave(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save room plan');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof RoomPlan, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex items-center justify-between">
          <CardTitle className="text-white">
            {roomPlan ? 'Edit Room Plan' : 'Add New Room Plan'}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-900/20 border border-red-800 rounded-md text-red-400">
                {error}
              </div>
            )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="plan_name" className="text-gray-300">Plan Name *</Label>
              <Input
                id="plan_name"
                value={formData.plan_name || ''}
                onChange={(e) => handleInputChange('plan_name', e.target.value)}
                placeholder="e.g., Standard Double, Deluxe Suite"
                required
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>

            <div>
              <Label htmlFor="meal_plan" className="text-gray-300">Meal Plan</Label>
              <select
                id="meal_plan"
                value={formData.meal_plan || 'Room Only'}
                onChange={(e) => handleInputChange('meal_plan', e.target.value)}
                className="w-full p-2 bg-gray-800 border border-gray-700 rounded-md text-white"
              >
                <option value="Room Only">Room Only</option>
                <option value="Breakfast">Breakfast</option>
                <option value="Half Board">Half Board</option>
                <option value="Full Board">Full Board</option>
                <option value="All Inclusive">All Inclusive</option>
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="meal_plan_description" className="text-gray-300">Meal Plan Description</Label>
            <Textarea
              id="meal_plan_description"
              value={formData.meal_plan_description || ''}
              onChange={(e) => handleInputChange('meal_plan_description', e.target.value)}
              placeholder="Describe what's included in the meal plan..."
              rows={3}
              className="bg-gray-800 border-gray-700 text-white"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="base_rate" className="text-gray-300">Base Rate (₹) *</Label>
              <Input
                id="base_rate"
                type="number"
                min="0"
                step="0.01"
                value={formData.base_rate || ''}
                onChange={(e) => handleInputChange('base_rate', parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                required
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>

            <div>
              <Label htmlFor="adults_included" className="text-gray-300">Adults Included *</Label>
              <Input
                id="adults_included"
                type="number"
                min="1"
                value={formData.adults_included || ''}
                onChange={(e) => handleInputChange('adults_included', parseInt(e.target.value) || 1)}
                placeholder="1"
                required
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>

            <div>
              <Label htmlFor="children_included" className="text-gray-300">Children Included</Label>
              <Input
                id="children_included"
                type="number"
                min="0"
                value={formData.children_included || ''}
                onChange={(e) => handleInputChange('children_included', parseInt(e.target.value) || 0)}
                placeholder="0"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="occupancy_type" className="text-gray-300">Occupancy Type</Label>
              <select
                id="occupancy_type"
                value={formData.occupancy_type || 'Double'}
                onChange={(e) => handleInputChange('occupancy_type', e.target.value)}
                className="w-full p-2 bg-gray-800 border border-gray-700 rounded-md text-white"
              >
                <option value="Single">Single</option>
                <option value="Double">Double</option>
                <option value="Triple">Triple</option>
                <option value="Quad">Quad</option>
              </select>
            </div>

            <div>
              <Label htmlFor="status" className="text-gray-300">Status</Label>
              <select
                id="status"
                value={formData.status || 'active'}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full p-2 bg-gray-800 border border-gray-700 rounded-md text-white"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="draft">Draft</option>
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_refundable"
              checked={formData.is_refundable || false}
              onChange={(e) => handleInputChange('is_refundable', e.target.checked)}
              className="rounded border-gray-600 bg-gray-800"
            />
            <Label htmlFor="is_refundable" className="text-gray-300">Refundable</Label>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {roomPlan ? 'Update' : 'Create'} Room Plan
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </div>
  </div>
  );
};

export default RoomPlanModal;
